/**
 * W1-11a (app-registry-keymap): `parseWhen` and the WhenContext builder (ARCHITECTURE.md §6.3, DESIGN.md §8.1).
 *
 * Grammar (whitespace-insensitive):
 *   expr  := and ('||' and)*
 *   and   := unary ('&&' unary)*
 *   unary := '!' unary | '(' expr ')' | atom | name ('==' | '!=') value
 * Atoms are the built-ins (BUILTIN_WHEN_ATOMS plus `focus.<region>` / `hover.<region>` for every data-region value:
 * BUILTIN_ATOM_SET) and names registered by modules (FeatureModule.whenAtoms); unknown atoms are errors. Module atoms
 * may not use the `focus.` / `hover.` prefixes (the router derives those from the DOM). Comparisons exist for
 * workspace, level, preset, tool and os, with their values validated.
 *
 * Specificity (the most specific applicable binding wins; equal specificity on overlapping contexts is a red
 * conflict): an atom or `==` comparison counts 2, a negation or `!=` counts 1 (a negative condition still holds
 * almost everywhere), `&&` adds, `||` takes the weakest branch, an empty condition is 0.
 *
 * `whenSatisfiable` decides whether conditions can hold at the same time (used for red/yellow conflict colouring
 * and to tell which bindings explicitly target a dialog), honouring the structure of the context: one focused
 * region at a time, one hover region, one placement state, selection kinds implying `sel.any`, etc.
 */

import type { MessageKey } from '../../i18n/types';
import type { ExperienceLevel, Selection, ToolId, WorkspaceId } from '../../state/types';
import type { Os } from './chords';
import { m } from './registry.messages';
import { translate, type Translate } from './text';
import { BUILTIN_WHEN_ATOMS, type AppServices, type KeymapPresetId, type ParseWhenFn, type WhenContext } from './types';

// ---------------------------------------------------------------------------------------------------------------
// AST
// ---------------------------------------------------------------------------------------------------------------

/** Comparison names and their allowed values. */
export const WHEN_COMPARISONS = {
  workspace: ['timing', 'chart', 'publish', 'library', 'settings'],
  level: ['guided', 'full'],
  preset: ['default', 'rpe'],
  tool: ['select', 'tap', 'drag', 'flick', 'hold', 'eraser'],
  os: ['mac', 'other'],
} as const satisfies Readonly<Record<string, readonly string[]>>;

export type WhenCmpName = keyof typeof WHEN_COMPARISONS;

/** Every `data-region` value (app/registry/types.ts; ARCHITECTURE.md §6.1). */
export const DATA_REGIONS = [
  'lane',
  'motion',
  'curve',
  'preview',
  'lines',
  'inspector',
  'timing',
  'dock',
  'problems',
  'history',
  'assets',
  'raw',
  'publish',
  'library',
  'settings',
] as const;

export type DataRegion = (typeof DATA_REGIONS)[number];

/** The dock's tabs: inside the dock, `focus.dock` / `hover.dock` hold together with these (and only these). */
export const DOCK_TABS: readonly DataRegion[] = ['lines', 'problems', 'history', 'assets', 'raw'];

/** The region atom prefixes; every name starting with one is the router's (modules may not register them). */
export const REGION_ATOM_PREFIXES = ['focus.', 'hover.'] as const;

/** `focus.<region>` and `hover.<region>` for every data-region value. */
export const REGION_ATOMS: readonly string[] = REGION_ATOM_PREFIXES.flatMap((prefix) =>
  DATA_REGIONS.map((r) => `${prefix}${r}`),
);

export type WhenAst =
  | { readonly t: 'true' }
  | { readonly t: 'atom'; readonly name: string }
  | { readonly t: 'cmp'; readonly name: WhenCmpName; readonly op: '==' | '!='; readonly value: string }
  | { readonly t: 'not'; readonly e: WhenAst }
  | { readonly t: 'and'; readonly items: readonly WhenAst[] }
  | { readonly t: 'or'; readonly items: readonly WhenAst[] };

export const TRUE_WHEN: WhenAst = { t: 'true' };

export interface CompiledWhen {
  readonly source: string;
  readonly ast: WhenAst;
  readonly specificity: number;
  readonly eval: (c: WhenContext) => boolean;
}

function isCmpName(name: string): name is WhenCmpName {
  return Object.hasOwn(WHEN_COMPARISONS, name);
}

// ---------------------------------------------------------------------------------------------------------------
// Tokenizer and parser
// ---------------------------------------------------------------------------------------------------------------

type Tok =
  | { readonly k: 'id'; readonly v: string }
  | { readonly k: 'str'; readonly v: string }
  | { readonly k: '(' | ')' | '!' | '&&' | '||' | '==' | '!='; readonly v: string };

class WhenError extends Error {}

function fail(t: Translate, key: MessageKey, params?: Readonly<Record<string, string | number>>): never {
  throw new WhenError(t(key, params));
}

const IDENT = /[A-Za-z0-9_][A-Za-z0-9_.-]*/y;

function tokenize(src: string, t: Translate): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src.charAt(i);
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    const two = src.slice(i, i + 2);
    if (two === '&&' || two === '||' || two === '==' || two === '!=') {
      out.push({ k: two, v: two });
      i += 2;
      continue;
    }
    if (c === '(' || c === ')' || c === '!') {
      out.push({ k: c, v: c });
      i++;
      continue;
    }
    if (c === "'" || c === '"') {
      const end = src.indexOf(c, i + 1);
      if (end < 0) fail(t, m.whenUnterminated);
      out.push({ k: 'str', v: src.slice(i + 1, end) });
      i = end + 1;
      continue;
    }
    IDENT.lastIndex = i;
    const id = IDENT.exec(src);
    if (id === null) fail(t, m.whenUnexpected, { token: c });
    out.push({ k: 'id', v: id[0] });
    i += id[0].length;
  }
  return out;
}

function parseTokens(toks: readonly Tok[], known: ReadonlySet<string>, t: Translate): WhenAst {
  let pos = 0;
  const peek = (): Tok | undefined => toks[pos];
  const next = (): Tok => {
    const tok = toks[pos++];
    if (tok === undefined) fail(t, m.whenUnexpectedEnd);
    return tok;
  };

  const parseOr = (): WhenAst => {
    const items = [parseAnd()];
    while (peek()?.k === '||') {
      pos++;
      items.push(parseAnd());
    }
    return items.length === 1 ? (items[0] as WhenAst) : { t: 'or', items };
  };
  const parseAnd = (): WhenAst => {
    const items = [parseUnary()];
    while (peek()?.k === '&&') {
      pos++;
      items.push(parseUnary());
    }
    return items.length === 1 ? (items[0] as WhenAst) : { t: 'and', items };
  };
  const parseUnary = (): WhenAst => {
    const tok = next();
    if (tok.k === '!') return { t: 'not', e: parseUnary() };
    if (tok.k === '(') {
      if (peek()?.k === ')') fail(t, m.whenEmptyGroup);
      const inner = parseOr();
      if (peek() === undefined) fail(t, m.whenUnclosed);
      const close = next();
      if (close.k !== ')') fail(t, m.whenUnexpected, { token: close.v });
      return inner;
    }
    if (tok.k !== 'id') fail(t, m.whenUnexpected, { token: tok.v });
    const op = peek();
    if (op?.k === '==' || op?.k === '!=') {
      pos++;
      if (!isCmpName(tok.v)) fail(t, m.whenUnknownName, { name: tok.v });
      const val = next();
      if (val.k !== 'id' && val.k !== 'str') fail(t, m.whenUnexpected, { token: val.v });
      const allowed: readonly string[] = WHEN_COMPARISONS[tok.v];
      if (!allowed.includes(val.v)) fail(t, m.whenBadValue, { value: val.v, name: tok.v, allowed: allowed.join(', ') });
      return { t: 'cmp', name: tok.v, op: op.k, value: val.v };
    }
    if (!known.has(tok.v)) fail(t, m.whenUnknownAtom, { atom: tok.v });
    return { t: 'atom', name: tok.v };
  };

  if (toks.length === 0) return TRUE_WHEN;
  const ast = parseOr();
  const rest = peek();
  if (rest !== undefined) fail(t, m.whenUnexpected, { token: rest.v });
  return ast;
}

/** Parse a condition into its AST; `{ error }` (translated) when it is invalid. */
export function parseWhenAst(
  expr: string,
  known: ReadonlySet<string>,
  t: Translate = translate,
): WhenAst | { readonly error: string } {
  try {
    return parseTokens(tokenize(expr, t), known, t);
  } catch (err) {
    if (err instanceof WhenError) return { error: err.message };
    throw err;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Evaluation and specificity
// ---------------------------------------------------------------------------------------------------------------

export function evalWhen(ast: WhenAst, c: WhenContext): boolean {
  switch (ast.t) {
    case 'true':
      return true;
    case 'atom':
      return c.has(ast.name);
    case 'cmp':
      return (c[ast.name] === ast.value) === (ast.op === '==');
    case 'not':
      return !evalWhen(ast.e, c);
    case 'and':
      return ast.items.every((x) => evalWhen(x, c));
    case 'or':
      return ast.items.some((x) => evalWhen(x, c));
  }
}

export function specificityOf(ast: WhenAst): number {
  switch (ast.t) {
    case 'true':
      return 0;
    case 'atom':
      return 2;
    case 'cmp':
      return ast.op === '==' ? 2 : 1;
    case 'not':
      return 1;
    case 'and':
      return ast.items.reduce((sum, x) => sum + specificityOf(x), 0);
    case 'or':
      return Math.min(...ast.items.map(specificityOf));
  }
}

/** Conjunction of conditions (TRUE_WHEN parts dropped). */
export function andWhen(...items: readonly WhenAst[]): WhenAst {
  const parts = items.filter((x) => x.t !== 'true');
  if (parts.length === 0) return TRUE_WHEN;
  return parts.length === 1 ? (parts[0] as WhenAst) : { t: 'and', items: parts };
}

export function compileAst(ast: WhenAst, source = ''): CompiledWhen {
  return { source, ast, specificity: specificityOf(ast), eval: (c) => evalWhen(ast, c) };
}

/** Parse and compile; `{ error }` (translated) when invalid. Empty or blank conditions always hold. */
export function compileWhen(
  expr: string,
  known: ReadonlySet<string>,
  t: Translate = translate,
): CompiledWhen | { readonly error: string } {
  const ast = parseWhenAst(expr, known, t);
  return 'error' in ast ? ast : compileAst(ast, expr);
}

/** Contracted export: parse a WhenExpr against the known atom names (built-in + registered). */
export const parseWhen: ParseWhenFn = (expr, knownAtoms) => {
  const c = compileWhen(expr, knownAtoms);
  return 'error' in c ? { error: c.error } : { eval: c.eval, specificity: c.specificity };
};

/** Every atom name used in a condition. */
export function atomsOf(ast: WhenAst, into: Set<string> = new Set()): Set<string> {
  switch (ast.t) {
    case 'atom':
      into.add(ast.name);
      break;
    case 'not':
      atomsOf(ast.e, into);
      break;
    case 'and':
    case 'or':
      for (const x of ast.items) atomsOf(x, into);
      break;
    default:
      break;
  }
  return into;
}

function cmpsOf(ast: WhenAst, into: Set<WhenCmpName> = new Set()): Set<WhenCmpName> {
  switch (ast.t) {
    case 'cmp':
      into.add(ast.name);
      break;
    case 'not':
      cmpsOf(ast.e, into);
      break;
    case 'and':
    case 'or':
      for (const x of ast.items) cmpsOf(x, into);
      break;
    default:
      break;
  }
  return into;
}

// ---------------------------------------------------------------------------------------------------------------
// Satisfiability (conflict analysis)
// ---------------------------------------------------------------------------------------------------------------

/**
 * Region atoms of which at most one holds at a time, for one prefix. They follow the region rule of the router
 * (FocusRegions.ts `activeRegionsOf`): only the closest `[data-region]` counts, plus the enclosing `dock` for the dock
 * and its tabs, so these atoms are exclusive however the panels are nested:
 *   - every region except the dock (one closest region);
 *   - the dock and every region outside it (the dock holds together with its tabs only).
 */
function regionGroups(prefix: (typeof REGION_ATOM_PREFIXES)[number]): Array<readonly string[]> {
  const tabs: ReadonlySet<string> = new Set(DOCK_TABS);
  return [
    DATA_REGIONS.filter((r) => r !== 'dock').map((r) => `${prefix}${r}`),
    DATA_REGIONS.filter((r) => !tabs.has(r)).map((r) => `${prefix}${r}`),
  ];
}

/** Atoms of which at most one holds at a time. */
const EXCLUSIVE_GROUPS: ReadonlyArray<readonly string[]> = [
  ...REGION_ATOM_PREFIXES.flatMap(regionGroups),
  ['placing.hold', 'placing.segment', 'paste.ghost'],
  ['sel.single', 'sel.mixed'],
  ['activeLine.uiMover', 'activeLine.notesAllowed'],
];

/** `a` implies `b`. */
const IMPLICATIONS: ReadonlyArray<readonly [string, string]> = [
  ['sel.notes', 'sel.any'],
  ['sel.keys', 'sel.any'],
  ['sel.segments', 'sel.any'],
  ['sel.lines', 'sel.any'],
  ['sel.bpm', 'sel.any'],
  ['sel.single', 'sel.any'],
  ['sel.mixed', 'sel.any'],
  ['placing.hold', 'project.open'],
  ['placing.segment', 'project.open'],
  ['paste.ghost', 'project.open'],
  ['activeLine.uiMover', 'project.open'],
  ['activeLine.notesAllowed', 'project.open'],
];

/** With `sel.single`, at most one selection kind is non-empty. */
const SELECTION_KINDS = ['sel.notes', 'sel.keys', 'sel.segments', 'sel.lines', 'sel.bpm'] as const;

/** Enumeration bound: beyond this many assignments the analysis assumes the conditions can overlap. */
const MAX_ASSIGNMENTS = 1 << 18;

export type FixedComparisons = Partial<Readonly<Record<WhenCmpName, string>>>;

function consistent(v: ReadonlyMap<string, boolean>): boolean {
  for (const group of EXCLUSIVE_GROUPS) {
    let n = 0;
    for (const a of group) if (v.get(a) === true) n++;
    if (n > 1) return false;
  }
  for (const [a, b] of IMPLICATIONS) if (v.get(a) === true && v.get(b) === false) return false;
  if (v.get('sel.single') === true) {
    let n = 0;
    for (const a of SELECTION_KINDS) if (v.get(a) === true) n++;
    if (n > 1) return false;
  }
  return true;
}

/** Can every condition hold at the same time (in some reachable context)? */
export function whenSatisfiable(asts: readonly WhenAst[], fixed: FixedComparisons = {}): boolean {
  const atoms = new Set<string>();
  const cmps = new Set<WhenCmpName>();
  for (const a of asts) {
    atomsOf(a, atoms);
    cmpsOf(a, cmps);
  }
  // Pull in implied atoms so implications between present atoms are checked.
  let grew = true;
  while (grew) {
    grew = false;
    for (const [a, b] of IMPLICATIONS) {
      if (atoms.has(a) && !atoms.has(b)) {
        atoms.add(b);
        grew = true;
      }
    }
  }
  const atomList = [...atoms];
  const cmpList = [...cmps];
  const domains = cmpList.map((name): readonly string[] => {
    const f = fixed[name];
    return f === undefined ? WHEN_COMPARISONS[name] : [f];
  });
  const cmpCombos = domains.reduce((n, d) => n * d.length, 1);
  if (atomList.length > 24 || cmpCombos * 2 ** atomList.length > MAX_ASSIGNMENTS) return true;

  const values = new Map<string, boolean>();
  const cmpValues: Partial<Record<WhenCmpName, string>> = { ...fixed };
  const ctx: WhenContext = {
    has: (atom) => values.get(atom) ?? false,
    get workspace() {
      return (cmpValues.workspace ?? 'chart') as WhenContext['workspace'];
    },
    get level() {
      return (cmpValues.level ?? 'full') as ExperienceLevel;
    },
    get preset() {
      return (cmpValues.preset ?? 'default') as KeymapPresetId;
    },
    get tool() {
      return cmpValues.tool ?? 'select';
    },
    get os() {
      return (cmpValues.os ?? 'other') as Os;
    },
  };

  for (let combo = 0; combo < cmpCombos; combo++) {
    let rest = combo;
    cmpList.forEach((name, i) => {
      const d = domains[i] ?? [];
      const v = d[rest % d.length];
      if (v !== undefined) cmpValues[name] = v;
      rest = Math.floor(rest / d.length);
    });
    for (let mask = 0; mask < 2 ** atomList.length; mask++) {
      atomList.forEach((a, i) => values.set(a, (mask & (1 << i)) !== 0));
      if (!consistent(values)) continue;
      if (asts.every((a) => evalWhen(a, ctx))) return true;
    }
  }
  return false;
}

/** Can both conditions hold at once? */
export function whenOverlap(a: WhenAst, b: WhenAst, fixed: FixedComparisons = {}): boolean {
  return whenSatisfiable([a, b], fixed);
}

/** Does the condition only hold when `atom` holds (e.g. a binding that explicitly targets dialogs)? */
export function whenRequires(ast: WhenAst, atom: string, fixed: FixedComparisons = {}): boolean {
  return !whenSatisfiable([ast, { t: 'not', e: { t: 'atom', name: atom } }], fixed);
}

// ---------------------------------------------------------------------------------------------------------------
// WhenContext builder
// ---------------------------------------------------------------------------------------------------------------

/** Built-in atom names: BUILTIN_WHEN_ATOMS plus the region atoms of every data-region value. */
export const BUILTIN_ATOM_SET: ReadonlySet<string> = new Set<string>([...BUILTIN_WHEN_ATOMS, ...REGION_ATOMS]);

/** Is this a built-in atom name, or one in the router's `focus.` / `hover.` namespace (not for modules)? */
export function isBuiltinAtomName(name: string): boolean {
  return BUILTIN_ATOM_SET.has(name) || REGION_ATOM_PREFIXES.some((prefix) => name.startsWith(prefix));
}

/** A value, or a function computing it on first use (DOM-derived inputs are only computed when a binding asks). */
export type Lazy<T> = T | (() => T);

/** Everything the context is derived from (the KeyboardRouter tracks the DOM parts). */
export interface WhenInputs {
  readonly services: AppServices | null;
  /** Region atoms of the focused element (FocusRegions.ts `activeRegionsOf`). */
  readonly focusRegions?: Lazy<readonly string[]>;
  /** Region atoms of the hovered element. */
  readonly hoverRegions?: Lazy<readonly string[]>;
  readonly textInput?: Lazy<boolean>;
  /** A modal dialog (or menu layer) is open. */
  readonly dialog?: Lazy<boolean>;
  /** Atoms forced on by the shell (paletteOpen, gesture, dialog, or any other name). */
  readonly flags?: ReadonlySet<string>;
  /** Module-registered atoms (FeatureModule.whenAtoms). */
  readonly moduleAtoms?: Readonly<Record<string, () => boolean>>;
  /** location.hash ('#/', '#/settings', '#/p/<id>/<workspace>'). */
  readonly route?: string;
  /** Defaults to services.keymap.preset. */
  readonly preset?: KeymapPresetId;
  readonly os: Os;
}

/** Memoised reader of a lazy input. */
function once<T>(input: Lazy<T> | undefined, fallback: T): () => T {
  if (typeof input !== 'function') {
    const v = input ?? fallback;
    return () => v;
  }
  const compute = input as () => T;
  let done = false;
  let v = fallback;
  return () => {
    if (!done) {
      v = compute();
      done = true;
    }
    return v;
  };
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

const WORKSPACES: ReadonlySet<string> = new Set<WorkspaceId>(['timing', 'chart', 'publish']);

function workspaceOf(route: string | undefined, services: AppServices | null): WhenContext['workspace'] {
  const r = route ?? '';
  if (r.startsWith('#/settings')) return 'settings';
  const match = /^#\/p\/[^/]+\/([^/?#]+)/.exec(r);
  if (match?.[1] !== undefined && WORKSPACES.has(match[1])) return match[1] as WorkspaceId;
  if (services === null || !safe(() => services.store !== null, false)) return 'library';
  return safe(() => services.editor.getState().workspace, 'chart');
}

function selectionCounts(sel: Selection): { readonly total: number; readonly kinds: number } {
  const sizes = [
    sel.notes.size,
    sel.keys.size,
    sel.segments.size,
    sel.lines.size,
    sel.bpm.size,
    sel.markers.size,
    sel.effects.size,
    sel.videos.size,
    sel.groups.size,
  ];
  return { total: sizes.reduce((a, b) => a + b, 0), kinds: sizes.filter((n) => n > 0).length };
}

/** Snapshot of the editor context; atoms are evaluated lazily on `has`. */
export function buildWhenContext(inputs: WhenInputs): WhenContext {
  const s = inputs.services;
  const flags = inputs.flags ?? new Set<string>();
  const focus = once<readonly string[]>(inputs.focusRegions, []);
  const hover = once<readonly string[]>(inputs.hoverRegions, []);
  const textInput = once(inputs.textInput, false);
  const dialog = once(inputs.dialog, false);
  const editor = s === null ? null : safe(() => s.editor.getState(), null);
  const store = s === null ? null : safe(() => s.store, null);
  const moduleAtoms = inputs.moduleAtoms ?? {};

  const activeLine = (): { readonly attachUI: unknown } | null => {
    if (editor === null || store === null || editor.activeLine === null) return null;
    const id = editor.activeLine;
    return safe(() => store.doc.lines.get(id) ?? null, null);
  };

  const builtin = (atom: string): boolean => {
    if (atom.startsWith('focus.')) return focus().includes(atom.slice(6));
    if (atom.startsWith('hover.')) return hover().includes(atom.slice(6));
    switch (atom) {
      case 'textInput':
        return textInput();
      case 'dialog':
        return dialog();
      case 'paletteOpen':
        return false;
      case 'placing.hold':
        return editor?.placement?.kind === 'hold';
      case 'placing.segment':
        return editor?.placement?.kind === 'segment';
      case 'paste.ghost':
        return editor?.placement?.kind === 'paste';
      case 'gesture':
        return store !== null && safe(() => store.gestureActive, false);
      case 'playing':
        return s !== null && safe(() => s.audio.transport.state.playing, false);
      case 'sel.notes':
        return (editor?.selection.notes.size ?? 0) > 0;
      case 'sel.keys':
        return (editor?.selection.keys.size ?? 0) > 0;
      case 'sel.segments':
        return (editor?.selection.segments.size ?? 0) > 0;
      case 'sel.lines':
        return (editor?.selection.lines.size ?? 0) > 0;
      case 'sel.bpm':
        return (editor?.selection.bpm.size ?? 0) > 0;
      case 'sel.any':
        return editor !== null && selectionCounts(editor.selection).total > 0;
      case 'sel.single':
        return editor !== null && selectionCounts(editor.selection).total === 1;
      case 'sel.mixed':
        return editor !== null && selectionCounts(editor.selection).kinds > 1;
      case 'column.focused':
        return (editor?.focusedColumn ?? null) !== null;
      case 'project.open':
        return store !== null;
      case 'activeLine.uiMover': {
        const line = activeLine();
        return line !== null && line.attachUI !== null;
      }
      case 'activeLine.notesAllowed': {
        const line = activeLine();
        return line !== null && line.attachUI === null;
      }
      default: {
        const fn = moduleAtoms[atom];
        return fn === undefined ? false : safe(fn, false);
      }
    }
  };

  const level = (): ExperienceLevel =>
    s === null ? 'full' : safe(() => s.settings.value.general.level, editor?.level ?? 'full');

  return {
    has: (atom) => flags.has(atom) || builtin(atom),
    workspace: workspaceOf(inputs.route, s),
    level: level(),
    preset: inputs.preset ?? (s === null ? 'default' : safe(() => s.keymap.preset, 'default')),
    tool: editor?.tool ?? ('select' satisfies ToolId),
    os: inputs.os,
  };
}
